import authService from '@/services/AuthService';
import { NavigationGuard } from 'vue-router';
import {
  ACTIVE_SESSIONS,
  BILLING_HISTORY,
  BILLING_PLAN_DETAILS,
  BILLING_PLANS,
  BROWSE_SHARED,
  CHECKOUT,
  COMPANY_NOT_PAID,
  COMPANY_NOT_PAID_FOR_USER,
  HOME,
  INACTIVE_COMPANY,
  USER_DEACTIVATED,
  USER_PROFILE,
} from '@/routerNames';
import { authRoutes } from '@/constants/RouterConsts';
import store from '@/store';
import { Logger } from '@/other/Logger';
import { DOMAIN_BLACK_LIST } from '@/constants/DomainConsts';
import { SNAPLORE_APP, hasGuidesAccess, isGuidesContext, paysOnlyForGuides } from '@/helpers/appContext';
import { getProductsUrl } from '@/helpers/productsUrl';

const log = new Logger('PaidCompanyMiddleware');

const billingRoutes = [CHECKOUT, BILLING_PLANS];
const sharedRoutes = [BROWSE_SHARED];
const skipRoutes = [COMPANY_NOT_PAID, COMPANY_NOT_PAID_FOR_USER];
// SNAP-2241: the only pages here a company that pays just for Guides still uses: account and billing, and the
// pages that tell a deactivated user or company why it is blocked.
const guidesOnlyPlanRoutes = [
  ...billingRoutes,
  ...sharedRoutes,
  BILLING_PLAN_DETAILS,
  BILLING_HISTORY,
  USER_PROFILE,
  ACTIVE_SESSIONS,
  USER_DEACTIVATED,
  INACTIVE_COMPANY,
];

// The host belongs to another company. SyncCompanyMiddleware, next in line, switches to it if this user is in it, so
// a link to a user's Snaplore company still works from a session that happens to be in their Guides-only one.
export const isOnAnotherCompanyHost = (
  company: { domain?: string | null; slug?: string } | null,
  hostname: string = window.location.hostname
): boolean => {
  const hostSlug = hostname.split('.')[0];
  return !!company?.domain && !DOMAIN_BLACK_LIST.includes(hostSlug) && company.slug !== hostSlug;
};

const PaidCompanyMiddleware: NavigationGuard = async (to, from, next) => {
  const company = store.state.company;
  const user = authService.user;

  const isShared = sharedRoutes.includes(to.name);
  const isOwner = user?.role === 'owner';

  if (user?.isSuperAdmin()) {
    next();
    return;
  }

  // SNAP-2241 (Roman, 2026-11-03): "if user paid just for guides he can access just guides". Snaplore
  // pages send such a company to the Guides app; the backend refuses its Snaplore-only API routes too.
  if (
    paysOnlyForGuides(company) &&
    !isOnAnotherCompanyHost(company) &&
    !guidesOnlyPlanRoutes.includes(to.name) &&
    !authRoutes.includes(to.name)
  ) {
    log.info('Company pays only for Guides, redirecting to the Guides app');
    window.location.href = getProductsUrl();
    return;
  }

  if (!company?.isPaid && skipRoutes.includes(to.name)) {
    if (!company?.paddle.status && isOwner) {
      next({ name: BILLING_PLANS });
      return;
    }
    next();
    return;
  }

  // D2: a company paid for something else (e.g. Snaplore/kb) landing on the
  // Guides plans page must see the upsell notice, not get bounced home —
  // that page IS the intended destination for this visit (SNAP-2241).
  const isGuidesUpsell = isGuidesContext(to.query) && company?.isPaid && !hasGuidesAccess(company);
  // The same the other way round (Roman, 2026-11-04): a company paying only for Guides can buy Snaplore.
  const isSnaploreUpsell = to.query?.app === SNAPLORE_APP && company?.isPaid && company?.productAccess?.kb === false;

  if (company?.isPaid && to.name === BILLING_PLANS && !isGuidesUpsell && !isSnaploreUpsell) {
    if (to.params?.update !== 'update') {
      log.info(`Company is already paid, redirecting to home page`);
      next({ name: HOME });
      return;
    }
  }

  if (company?.isPaid && (to.name === BILLING_PLANS || to.name === CHECKOUT) && !isOwner) {
    next({ name: HOME });
  }

  if ((isShared || !company) && !user) {
    next();
    return;
  }

  if (!company?.isPaid && !billingRoutes.includes(to.name) && !authRoutes.includes(to.name)) {
    if (!isOwner) {
      next({ name: COMPANY_NOT_PAID_FOR_USER });
      return;
    }

    log.info(`Company is NOT paid, redirecting to billing ${COMPANY_NOT_PAID}`);
    next({ name: COMPANY_NOT_PAID });
    return;
  }

  next();
};

export default PaidCompanyMiddleware;
